import type { DefaultPurchaseSettingSchema } from '~/schemas/default_purchase_setting';
import type { PositionInfoSchema } from '~/schemas/position_info_schema';

const DEFAULT_MENU_ID = '';
const DEFAULT_QUANTITY = 1;
const DEFAULT_PAYMENT_METHOD = '';

const getDefaultMenuId = (
  positionInfo: PositionInfoSchema,
  defaultSetting: DefaultPurchaseSettingSchema,
) => {
  // 未設定ならデフォルトメニューIDを返す
  if (!defaultSetting.default_menu) {
    return DEFAULT_MENU_ID;
  }

  // デフォルトメニューIDが設定されている場合
  // 販売していない商品または注文可能数が0の場合はデフォルトメニューIDを返す
  const price = positionInfo.prices?.find(
    (price) => price.menu.id === defaultSetting.default_menu?.id,
  );
  if (!price || price.order_limit === 0) {
    return DEFAULT_MENU_ID;
  }

  // 注文可能であればユーザーが設定したデフォルトメニューIDを返す
  return defaultSetting.default_menu.id;
};

const getDefaultQuantity = (
  positionInfo: PositionInfoSchema,
  defaultSetting: DefaultPurchaseSettingSchema,
) => {
  // 数量未設定ならデフォルト数量を返す
  if (!defaultSetting.default_quantity) {
    return DEFAULT_QUANTITY;
  }

  // デフォルト数量が設定されている場合
  // デフォルト数量が注文可能数を超えている場合は注文可能数を返す
  // それ以外はユーザーが設定したデフォルト数量を返す
  const menuId = getDefaultMenuId(positionInfo, defaultSetting);
  const price = positionInfo.prices?.find((price) => price.menu.id === menuId);
  if (!price) {
    // メニューが見つからない場合はユーザーが設定したデフォルト数量を返す
    // NOTE: menu_id が未設定の場合もここに入る
    return defaultSetting.default_quantity;
  }

  return Math.min(defaultSetting.default_quantity, price.order_limit);
};

const getDefaultPaymentMethod = (
  positionInfo: PositionInfoSchema,
  defaultSetting: DefaultPurchaseSettingSchema,
) => {
  // 未設定ならデフォルト支払い方法を返す
  if (!defaultSetting.default_payment_method) {
    return DEFAULT_PAYMENT_METHOD;
  }

  // デフォルト支払い方法が設定されている場合
  // 対応していない支払い方法の場合はデフォルト支払い方法を返す
  // それ以外はユーザーが設定したデフォルト支払い方法を返す
  if (
    !positionInfo.payment_methods.some(
      (method) => method.code === defaultSetting.default_payment_method,
    )
  ) {
    return DEFAULT_PAYMENT_METHOD;
  }

  return defaultSetting.default_payment_method;
};

export const getInitialPurchaseSetting = (
  positionInfo: PositionInfoSchema | null,
  defaultSetting: DefaultPurchaseSettingSchema | null,
): {
  menu_id: string;
  quantity: number;
  payment_method: string;
} => {
  if (!positionInfo || !defaultSetting) {
    return {
      menu_id: DEFAULT_MENU_ID,
      quantity: DEFAULT_QUANTITY,
      payment_method: DEFAULT_PAYMENT_METHOD,
    };
  }

  return {
    menu_id: getDefaultMenuId(positionInfo, defaultSetting),
    quantity: getDefaultQuantity(positionInfo, defaultSetting),
    payment_method: getDefaultPaymentMethod(positionInfo, defaultSetting),
  };
};
